import React, { useState } from 'react'
import Select from 'react-select'
import { Form, Row, Col, InputGroup, Modal, Button, Alert } from 'react-bootstrap'
import { camelCase } from 'lodash'
import { handleBinaryFile, isLayoutEditableStatus } from '../article'
import * as Yup from 'yup'
import { useAuth, shouldShowExperimentalFeatures } from '../auth'
import type { FormikErrors, FormikTouched } from 'formik'
import type { AlertState, StatusValue } from '../common'
import type { Media, Setting } from '../media'
import type { User } from '../user'
import type { CurrentUser } from '../auth'

type PageEnumValues = {
  statuses: Record<string, string>
  columnSettings: Record<string, string>
  pageTypes: Record<string, string>
}

type PageMarginSelection = { pageMarginSettingId: number }

interface PageUpsertFormValues {
  mediaId: number | string
  settingId: number | string
  selectedSettingList: Setting[]
  columnSetting: string
  numberOfCharactersPerRow: number | string
  numberOfRows: number | string
  numberOfColumns: number | string
  assigneeId: number | string
  issueDate: string
  issueNumber: number | string
  pageNumber: number | string
  pageRevision: number | string
  pageType: string
  pageMarginRegion: string
  pageMarginOtherSelections: PageMarginSelection[]
  bottomAdNumberOfColumns: number | string
  templateFile: string | undefined
  templateFilename: string
  replaceFile: boolean
  deleteFile: boolean
  loadedBinaryFile: unknown
  status: string | undefined
  nonFoldingLayout: boolean
}

interface TemplateFileValues {
  templateFilename?: string
  templateFile?: string
  replaceFile?: boolean
  deleteFile?: boolean
}

interface TemplateFileControlProps {
  values: TemplateFileValues
  handleBlur: React.FocusEventHandler<HTMLElement>
  handleChange: React.ChangeEventHandler<HTMLElement>
  setFieldValue: (field: string, value: unknown) => void
  disabled: boolean
}

export const getPageMarginRelatedLists = (selectedSetting: Setting | undefined) => {
  const pageMarginSettings = selectedSetting?.pageMarginSettings
  const pageMarginOtherSettings = selectedSetting?.pageMarginOtherSettings
  const pageMarginRegionList = pageMarginSettings
    ?.filter((s) => s.pageMarginSettingType === 'region')
    ?.map((s) => s.name)
  const pageMarginOtherList = pageMarginSettings
    ?.filter((s) => s.pageMarginSettingType === 'other')
    ?.map((s) => {
      const keyName = pageMarginOtherSettings?.find((o) => o.id === s.pageMarginOtherSettingId)?.name
      return { value: s.id, label: `${keyName}:${s.name}` }
    })
  return { pageMarginSettings, pageMarginRegionList, pageMarginOtherList }
}

export const TemplateFileControl = ({
  values,
  handleBlur,
  handleChange,
  setFieldValue,
  disabled
}: TemplateFileControlProps) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const loadedBinaryAsync = handleBinaryFile(file)

    loadedBinaryAsync.then((value) => {
      setFieldValue('loadedBinaryFile', value)
    })

    handleChange(e)
  }
  if ((values?.templateFilename?.length ?? 0) > 0) {
    return (
      <Row>
        <Col sm={8}>
          <Form.Control
            size='sm'
            name='templateFilename'
            defaultValue={values.templateFilename}
            readOnly
            hidden={values.replaceFile}
          />
          <InputGroup>
            <Form.Control
              id='templateFileInput'
              name='templateFile'
              size='sm'
              type='file'
              accept='.indt'
              value={values.templateFile ?? ''}
              onChange={handleFileChange}
              onBlur={handleBlur}
              hidden={!values.replaceFile}
              disabled={disabled}
            />
          </InputGroup>
        </Col>
        <Col sm={4}>
          <Col>
            <Form.Check
              type='switch'
              id='replaceFileSwitch'
              label='変更'
              name='replaceFile'
              checked={values.replaceFile ?? false}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disabled || values.deleteFile}
              inline
            />
            <Form.Check
              type='switch'
              id='deleteFileSwitch'
              label='削除'
              name='deleteFile'
              checked={values.deleteFile ?? false}
              onChange={(e) => {
                setFieldValue('replaceFile', false)
                handleChange(e)
              }}
              onBlur={handleBlur}
              disabled={disabled}
              inline
            />
          </Col>
        </Col>
      </Row>
    )
  } else {
    return (
      <InputGroup>
        <Form.Control
          id='templateFileInput'
          name='templateFile'
          size='sm'
          type='file'
          accept='.indt'
          value={values.templateFile ?? ''}
          onChange={handleFileChange}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </InputGroup>
    )
  }
}

interface PageUpsertFormProps {
  values: PageUpsertFormValues
  errors: FormikErrors<PageUpsertFormValues>
  touched: FormikTouched<PageUpsertFormValues>
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleBlur: React.FocusEventHandler<HTMLElement>
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  isValid: boolean
  setFieldValue: (field: string, value: unknown) => void
  fetching: boolean
  enumValues: PageEnumValues
  onClickCancel: () => void
  title: string
  submitButtonText: string
  mediaList?: Media[]
  settingList?: Setting[]
  userList?: User[]
  currentUser: CurrentUser
  alert?: AlertState | null
}

const PageUpsertFormBase = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isValid,
  setFieldValue,
  fetching,
  enumValues,
  onClickCancel,
  title,
  submitButtonText,
  mediaList,
  settingList,
  userList,
  currentUser,
  alert
}: PageUpsertFormProps) => {
  const settingId = values.settingId
  const selectedSetting = values.selectedSettingList?.find((s) => s.id === settingId)
  const {
    pageMarginSettings: settings,
    pageMarginRegionList: regionList,
    pageMarginOtherList: otherList
  } = getPageMarginRelatedLists(selectedSetting)

  const selectionIds = new Set(values.pageMarginOtherSelections?.map((s) => s.pageMarginSettingId) || [])
  const defaultOtherList = otherList?.filter((o) => selectionIds.has(o.value)) || []

  const [pageMarginSettings, setPageMarginSettings] = useState(settings)
  const [pageMarginRegionList, setPageMarginRegionList] = useState(regionList)
  const [pageMarginOtherList, setPageMarginOtherList] = useState(otherList)
  const isLayoutableWritable = values.status === undefined || isLayoutEditableStatus(values.status as StatusValue)
  const auth = useAuth()
  const showExperimentalFeatures = shouldShowExperimentalFeatures(auth)

  const setSelectedSetting = (selectedSetting: Setting | undefined) => {
    const selectedColumnSetting = selectedSetting?.columnSetting
    setFieldValue('columnSetting', selectedColumnSetting?.typeOf || '')
    setFieldValue('numberOfColumns', selectedColumnSetting?.numberOfColumns || '')
    setFieldValue('numberOfRows', selectedColumnSetting?.numberOfRows || '')
    setFieldValue('numberOfCharactersPerRow', selectedColumnSetting?.numberOfCharactersPerRow || '')
    const {
      pageMarginSettings: settings,
      pageMarginRegionList: regionList,
      pageMarginOtherList: otherList
    } = getPageMarginRelatedLists(selectedSetting)
    setPageMarginSettings(settings)
    setPageMarginRegionList(regionList)
    setPageMarginOtherList(otherList)
    if ((regionList?.length ?? 0) > 0) {
      setFieldValue('pageMarginRegion', regionList?.[0])
    } else {
      setFieldValue('pageMarginRegion', '')
    }
    if ((otherList?.length ?? 0) > 0) {
      setFieldValue('pageMarginOther', otherList?.[0])
    } else {
      setFieldValue('pageMarginOther', '')
    }
  }
  const handleChangeSetting = (e: React.ChangeEvent<HTMLElement>) => {
    const settingId = parseInt((e.target as HTMLSelectElement).value)
    const selectedSetting = values.selectedSettingList?.find((s) => s.id === settingId)
    setSelectedSetting(selectedSetting)
    handleChange(e)
  }
  const handleChangePageNumber = (e: React.ChangeEvent<HTMLElement>) => {
    const pageNumber = parseInt((e.target as HTMLInputElement).value)
    const isEven = pageNumber % 2 === 0
    const pageType = isEven ? 'rightPageSpread' : 'leftPageSpread'
    setFieldValue('pageType', pageType)
    handleChange(e)
  }
  return (
    <>
      <Modal.Header>
        <Modal.Title>
          {title}
          {!isLayoutableWritable && (
            <span className='text-secondary'> ({enumValues.statuses[camelCase(values.status)]}のため編集不可)</span>
          )}
        </Modal.Title>
      </Modal.Header>
      {alert?.show && alert?.alertBody && (
        <Alert className='upsert-page-errors-alert' variant='danger'>
          {alert.alertBody}
        </Alert>
      )}
      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Form.Group as={Col}>
              <Form.Label column className='mediaIdLabel'>
                媒体名
              </Form.Label>
              <Col>
                <Form.Control
                  as='select'
                  type='text'
                  name='mediaId'
                  size='sm'
                  value={values.mediaId}
                  onChange={(e) => {
                    const mediaId = parseInt(e.target.value)
                    const selectedSettingList = settingList?.filter((s) => s.media.id === mediaId) || []
                    const settingId = selectedSettingList?.length > 0 ? selectedSettingList[0].id : ''
                    const selectedSetting = selectedSettingList?.find((s) => s.id === settingId)
                    setFieldValue('selectedSettingList', selectedSettingList)
                    setFieldValue('settingId', settingId)
                    setSelectedSetting(selectedSetting)
                    handleChange(e)
                  }}
                  onBlur={handleBlur}
                  isInvalid={!!(touched.mediaId && errors.mediaId)}
                  disabled={!isLayoutableWritable || (mediaList?.length ?? 0) <= 1}
                >
                  {mediaList?.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type='invalid'>{errors.mediaId}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column className='mediaIdLabel'>
                紙面ベース
              </Form.Label>
              <Col>
                <Form.Control
                  as='select'
                  type='text'
                  name='settingId'
                  size='sm'
                  value={values.settingId}
                  onChange={handleChangeSetting}
                  onBlur={handleBlur}
                  isInvalid={!!(touched.settingId && errors.settingId)}
                  disabled={!isLayoutableWritable || !!errors.mediaId || (settingList?.length ?? 0) <= 1}
                >
                  {values.selectedSettingList?.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type='invalid'>{errors.settingId}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          </Row>
          <Row className='mb-3'>
            <Form.Group as={Col}>
              <Form.Label column>段組</Form.Label>
              <Col>
                <Form.Control
                  as='select'
                  type='text'
                  name='columnSetting'
                  size='sm'
                  value={values.columnSetting}
                  onChange={(event) => {
                    if (event.target.value === 'regular') {
                      // TODO: copy from page base setting
                    }
                    handleChange(event)
                  }}
                  onBlur={handleBlur}
                  isInvalid={!!(touched.columnSetting && errors.columnSetting)}
                >
                  {Object.keys(enumValues.columnSettings).map((k) => (
                    <option key={k} value={k}>
                      {enumValues.columnSettings[k]}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type='invalid'>{errors.columnSetting}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column>字数</Form.Label>
              <Col>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    type='number'
                    name='numberOfCharactersPerRow'
                    size='sm'
                    value={values.numberOfCharactersPerRow}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.numberOfCharactersPerRow && errors.numberOfCharactersPerRow)}
                    disabled={!isLayoutableWritable || values.columnSetting === 'regular'}
                  />
                  <InputGroup.Text>字</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.numberOfCharactersPerRow}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column>行数</Form.Label>
              <Col>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    type='number'
                    name='numberOfRows'
                    size='sm'
                    value={values.numberOfRows}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.numberOfRows && errors.numberOfRows)}
                    disabled={!isLayoutableWritable || values.columnSetting === 'regular'}
                  />
                  <InputGroup.Text>行</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.numberOfRows}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column>段数</Form.Label>
              <Col>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    type='number'
                    name='numberOfColumns'
                    size='sm'
                    value={values.numberOfColumns}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.numberOfColumns && errors.numberOfColumns)}
                    disabled={!isLayoutableWritable || values.columnSetting === 'regular'}
                  />
                  <InputGroup.Text>段</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.numberOfColumns}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            {showExperimentalFeatures && (
              <Form.Group as={Col}>
                <Form.Label column>総流し</Form.Label>
                <Col>
                  <Form.Check
                    type='switch'
                    id='nonFoldingLayoutSwitch'
                    label='総流し'
                    name='nonFoldingLayout'
                    checked={values.nonFoldingLayout ?? false}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!isLayoutableWritable}
                    isInvalid={!!(touched.nonFoldingLayout && errors.nonFoldingLayout)}
                    inline
                  />
                </Col>
              </Form.Group>
            )}
          </Row>
          {currentUser.role === 'group_administrator' && (
            <Form.Group as={Row}>
              <Form.Label column className='col-2'>
                担当者
              </Form.Label>
              <Col>
                <Form.Control
                  as='select'
                  type='text'
                  name='assigneeId'
                  size='sm'
                  value={values.assigneeId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!(touched.assigneeId && errors.assigneeId)}
                  disabled={!isLayoutableWritable || (userList?.length ?? 0) <= 1}
                >
                  {userList?.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type='invalid'>{errors.assigneeId}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          )}
          <Row className='mb-3'>
            <Form.Group as={Col}>
              <Form.Label column className='issueDateLabel'>
                発行日
              </Form.Label>
              <Col>
                <Form.Control
                  type='date'
                  name='issueDate'
                  size='sm'
                  value={values.issueDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!(touched.issueDate && errors.issueDate)}
                  disabled={!isLayoutableWritable}
                />
                <Form.Control.Feedback type='invalid'>{errors.issueDate}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column className='issueNumberLabel'>
                号数
              </Form.Label>
              <Col>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    type='number'
                    name='issueNumber'
                    size='sm'
                    value={values.issueNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.issueNumber && errors.issueNumber)}
                    disabled={!isLayoutableWritable}
                  />
                  <InputGroup.Text>号</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.issueNumber}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column className='pageNumberLabel'>
                面数
              </Form.Label>
              <Col>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    type='number'
                    name='pageNumber'
                    size='sm'
                    value={values.pageNumber}
                    onChange={handleChangePageNumber}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.pageNumber && errors.pageNumber)}
                    disabled={!isLayoutableWritable}
                  />
                  <InputGroup.Text>面</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.pageNumber}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column className='pageRevisionLabel'>
                版数
              </Form.Label>
              <Col>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    type='number'
                    name='pageRevision'
                    size='sm'
                    value={values.pageRevision}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.pageRevision && errors.pageRevision)}
                    disabled={!isLayoutableWritable}
                  />
                  <InputGroup.Text>版</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.pageRevision}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label column>紙面種別</Form.Label>
              <Col>
                <Form.Control
                  as='select'
                  type='text'
                  name='pageType'
                  size='sm'
                  value={values.pageType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!(touched.pageType && errors.pageType)}
                  disabled={!isLayoutableWritable}
                >
                  {Object.keys(enumValues.pageTypes).map((k) => (
                    <option key={k} value={k}>
                      {enumValues.pageTypes[k]}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type='invalid'>{errors.pageType}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          </Row>
          {pageMarginSettings !== undefined && pageMarginSettings.length > 0 && (
            <Row className='mb-3'>
              <Form.Group as={Col}>
                <Form.Label column>欄外地方</Form.Label>
                <Col>
                  <Form.Control
                    as='select'
                    type='text'
                    name='pageMarginRegion'
                    size='sm'
                    value={values.pageMarginRegion}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.pageMarginRegion && errors.pageMarginRegion)}
                    disabled={!isLayoutableWritable || (pageMarginRegionList?.length ?? 0) === 0}
                  >
                    <option value='' />
                    {pageMarginRegionList?.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type='invalid'>{errors.pageMarginRegion}</Form.Control.Feedback>
                </Col>
              </Form.Group>
              <Form.Group as={Col}>
                <Form.Label column>欄外その他</Form.Label>
                <Col>
                  <Select
                    name='pageMarginOtherSelections'
                    isMulti
                    closeMenuOnSelect={false}
                    options={pageMarginOtherList}
                    defaultValue={defaultOtherList}
                    onChange={(selected) => {
                      const pageMarginOtherSelections = selected?.map((s) => s.value) ?? []
                      setFieldValue('pageMarginOtherSelections', pageMarginOtherSelections)
                    }}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.pageMarginOtherSelections as string}
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>
            </Row>
          )}
          <Row>
            <Form.Group as={Col} sm={3}>
              <Form.Label column>記事下広告分の余白</Form.Label>
              <Col>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    id='bottomAdColumnsInput'
                    type='number'
                    name='bottomAdNumberOfColumns'
                    value={values.bottomAdNumberOfColumns}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!(touched.bottomAdNumberOfColumns && errors.bottomAdNumberOfColumns)}
                    disabled={!isLayoutableWritable}
                  />
                  <InputGroup.Text>段</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.bottomAdNumberOfColumns}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Col} sm={9}>
              <Form.Label column>紙面テンプレートファイル(indtファイル)</Form.Label>
              <Col>
                <TemplateFileControl
                  key='templateFileControl'
                  {...{
                    values,
                    errors,
                    handleBlur,
                    handleChange,
                    setFieldValue,
                    disabled: !isLayoutableWritable
                  }}
                />
              </Col>
            </Form.Group>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClickCancel}>
            キャンセル
          </Button>
          <Button type='submit' disabled={!isLayoutableWritable || !isValid || fetching}>
            {submitButtonText}
          </Button>
        </Modal.Footer>
      </Form>
    </>
  )
}

export const PageUpsertForm = Object.assign(PageUpsertFormBase, {
  schema: Yup.object().shape({
    mediaId: Yup.number().required(),
    settingId: Yup.number().required(),
    columnSetting: Yup.string().required(),
    numberOfCharactersPerRow: Yup.number().required().min(10).max(30),
    numberOfRows: Yup.number().required().min(30).max(100),
    numberOfColumns: Yup.number().required().min(1).max(15),
    assigneeId: Yup.number().required(),
    pageType: Yup.string().required(),
    issueDate: Yup.date().required(),
    issueNumber: Yup.number().required().min(1),
    pageNumber: Yup.number().required().min(1),
    pageRevision: Yup.number().required().min(1),
    pageMarginRegion: Yup.string(),
    pageMarginOtherSelections: Yup.array().of(Yup.number()),
    bottomAdNumberOfColumns: Yup.number().required().min(0),
    templateFilename: Yup.string().nullable(),
    deleteFile: Yup.boolean(),
    nonFoldingLayout: Yup.boolean(),
    status: Yup.string().nullable()
  })
})
